// apps/widget/src/lib/sse.ts
//
// Scan progress over SSE. [ADR 0007]
//
// EventSource reconnects natively, which is most of why this is not a
// WebSocket — but it reconnects forever, including after the scan is finished,
// so closing deliberately matters.
//
// Some networks accept the stream and then deliver nothing until it ends —
// buffering proxies, and Cloudflare quick tunnels in development. The server
// sends a `ready` event the moment the stream opens; if it has not arrived
// within a few seconds, this falls back to polling the session's state. The
// state is also read once on subscribe, so an event sent before this screen
// was listening (a fast analysis, a screen change) is never missed.

// The widget's own setting first: in development the widget (opened on a
// phone) reaches the API through a public tunnel, while the dashboard keeps
// using localhost — its sign-in cookie is not sent to another site.
const API_BASE =
  import.meta.env['VITE_WIDGET_API_URL'] ?? import.meta.env['VITE_API_URL'] ?? 'https://api.skinvizo.com/v1';

export type ScanEventType =
  | 'connected'
  | 'uploaded'
  | 'analyzing'
  | 'progress'
  | 'complete'
  | 'failed'
  | 'ready'
  | 'ping';

export interface ScanEvent {
  sessionId: string;
  type: ScanEventType;
  label?: string;
  scanId?: string;
  thumbnailUrl?: string;
  data?: Record<string, unknown>;
  error?: { code: string; message: string };
}

export interface ScanStreamHandlers {
  onEvent?: (event: ScanEvent) => void;
  onComplete?: (scanId: string, data: Record<string, unknown>) => void;
  onFailed?: (error: { code: string; message: string; fixes?: string[] }) => void;
  /** Fired when the connection drops and cannot recover. */
  onStalled?: () => void;
}

export interface ScanStream {
  close(): void;
}

export function subscribeToScan(
  sessionId: string,
  key: string,
  handlers: ScanStreamHandlers,
): ScanStream {
  const url = new URL(`${API_BASE}/public/scan-sessions/${sessionId}/events`);
  url.searchParams.set('key', key);

  const source = new EventSource(url.toString());
  let closed = false;
  let streaming = false;
  let stallTimer: ReturnType<typeof setTimeout>;
  let pollTimer: ReturnType<typeof setInterval> | undefined;
  // One-off milestones are delivered once, whichever path saw them first.
  const seen = new Set<ScanEventType>();

  // The server heartbeats every 25s. Missing two in a row means the stream is
  // dead behind a proxy that is holding the socket open — the failure mode that
  // otherwise shows as a frozen analysing screen with no error.
  const resetStallTimer = (): void => {
    clearTimeout(stallTimer);
    stallTimer = setTimeout(() => {
      if (!closed) handlers.onStalled?.();
    }, 60_000);
  };

  const deliver = (event: ScanEvent): void => {
    if (closed) return;
    if (event.type === 'ready') {
      // Events really flow; the stream can be trusted on its own.
      streaming = true;
      stopPolling();
      return;
    }
    if (event.type === 'ping') return;
    if (event.type !== 'progress') {
      if (seen.has(event.type)) return;
      seen.add(event.type);
    }

    handlers.onEvent?.(event);

    if (event.type === 'complete' && event.scanId) {
      handlers.onComplete?.(event.scanId, event.data ?? {});
      close();
    }

    if (event.type === 'failed') {
      const error = event.error ?? { code: 'ANALYSIS_FAILED', message: 'Analysis failed.' };
      // A rejected photo carries what to change — pass it on, or the visitor
      // retakes the same photo and gets the same answer.
      const fixes = event.data?.['fixes'];
      handlers.onFailed?.(
        Array.isArray(fixes) && fixes.length > 0 ? { ...error, fixes: fixes.map(String) } : error,
      );
      close();
    }
  };

  const handle = (raw: string): void => {
    let event: ScanEvent;
    try {
      event = JSON.parse(raw) as ScanEvent;
    } catch {
      return;
    }
    resetStallTimer();
    deliver(event);
  };

  /** Reads where the session has got to and replays any milestone not yet seen. */
  const sync = async (): Promise<void> => {
    const stateUrl = new URL(`${API_BASE}/public/scan-sessions/${sessionId}/state`);
    stateUrl.searchParams.set('key', key);
    let state: SessionState;
    try {
      const response = await fetch(stateUrl.toString());
      if (!response.ok) return;
      state = (await response.json()) as SessionState;
    } catch {
      return; // The next poll, or the stream, will catch up.
    }
    if (closed) return;
    resetStallTimer();

    if (state.phoneConnected) deliver({ sessionId, type: 'connected', label: 'Phone connected' });
    const scan = state.scan;
    if (!scan) return;

    deliver({
      sessionId,
      type: 'uploaded',
      scanId: scan.id,
      label: 'Photo received',
      ...(scan.thumbnailUrl ? { thumbnailUrl: scan.thumbnailUrl } : {}),
    });
    if (scan.status === 'UPLOADED' || scan.status === 'ANALYZING' || scan.status === 'COMPLETE') {
      deliver({ sessionId, type: 'analyzing', scanId: scan.id });
    }
    if (scan.status === 'COMPLETE') {
      deliver({ sessionId, type: 'complete', scanId: scan.id, data: scan.result ?? {} });
    } else if (scan.status === 'REJECTED_QUALITY') {
      deliver({
        sessionId,
        type: 'failed',
        error: { code: 'QUALITY_GATE_FAILED', message: 'That photo could not be used. Try again facing the camera, in even light.' },
      });
    } else if (scan.status === 'FAILED') {
      deliver({
        sessionId,
        type: 'failed',
        error: { code: 'ANALYSIS_FAILED', message: 'We could not analyse that photo. Please try again.' },
      });
    }
  };

  const startPolling = (): void => {
    if (closed || pollTimer) return;
    pollTimer = setInterval(() => void sync(), POLL_EVERY_MS);
  };

  const stopPolling = (): void => {
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = undefined;
  };

  const close = (): void => {
    if (closed) return;
    closed = true;
    clearTimeout(stallTimer);
    clearTimeout(fallbackTimer);
    stopPolling();
    source.close();
  };

  source.onmessage = (e) => handle(e.data as string);

  // Nest's @Sse names each event by type, so the default onmessage handler never
  // fires for them — each has to be bound explicitly.
  for (const type of ['ready', 'connected', 'uploaded', 'analyzing', 'progress', 'complete', 'failed'] as const) {
    source.addEventListener(type, (e) => handle((e as MessageEvent).data as string));
  }

  source.onerror = () => {
    if (closed) return;
    // A dropped stream is no longer proof that events flow. A buffering proxy
    // also hands over everything it held — `ready` included — only as the
    // stream ends, which would otherwise switch polling off for good.
    streaming = false;
    startPolling();
    // EventSource retries on its own; only a permanently closed stream is a
    // real failure worth surfacing.
    if (source.readyState === EventSource.CLOSED) handlers.onStalled?.();
  };

  // Catch up once now, then poll only if the stream has not proven itself.
  void sync();
  const fallbackTimer = setTimeout(() => {
    if (!streaming) startPolling();
  }, READY_WITHIN_MS);

  resetStallTimer();
  return { close };
}

/** How long a working stream takes to say `ready`, before polling takes over. */
const READY_WITHIN_MS = 4_000;
const POLL_EVERY_MS = 2_000;

interface SessionState {
  sessionId: string;
  phoneConnected: boolean;
  scan: {
    id: string;
    status: 'PENDING' | 'UPLOADED' | 'ANALYZING' | 'COMPLETE' | 'FAILED' | 'REJECTED_QUALITY';
    result?: Record<string, unknown>;
    thumbnailUrl?: string;
  } | null;
}
